import { translate } from '@avensia/excite';
import { styled } from '@glitz/react';
import { StockStatus } from 'svenssons/product/StockStatus';
import * as React from 'react';
import { type PartPropType } from 'webapp/product-and-package-utils';
import ProductVariantSelector from './ProductVariantsSelector';
import { useVariantSelector } from 'webapp/variant-selectors/ProductVariantsSelectorProvider';

export type SelectedPart = Excite.Commercetools.PackagePartRequest & {
  variant: NordicNest.Api.VariantLinkApiModel;
};

type PropType = {
  packageParts: NordicNest.Api.PackagePartApiModel[];
  variants: NordicNest.Api.VariantLinkApiModel[];
};

const showStockStatus = ['outOfStock', 'preOrdered', 'backOrdered'];

type PartType = Omit<PartPropType, 'selectedParts' | 'setSelectedParts'>;

function Part({ part, variants }: PartType) {
  const { value, onChangeOption } = useVariantSelector(part.key);
  const selectedVariant = variants.find(v => v.articleNumber === value);

  const onRemoveOption = () => onChangeOption?.('');

  return (
    <PartContainer key={part.key}>
      <BundleVariantSelector selectorId={part.key} onRemoveOption={part.isOptional ? onRemoveOption : undefined} />
      {selectedVariant && showStockStatus.includes(selectedVariant.stockStatus) && (
        <StockStatusContainer>
          <StockStatus
            variantStatus={selectedVariant.variantStatus}
            stockStatus={selectedVariant.stockStatus}
            deliveryTime={selectedVariant.deliveryText}
          />
        </StockStatusContainer>
      )}
    </PartContainer>
  );
}

export function PackageParts(props: PropType) {
  const requrieParts = props.packageParts.filter(part => !part.isOptional);
  const optionalParts = props.packageParts.filter(part => part.isOptional);
  return (
    <Parts>
      {requrieParts.map(part => (
        <Part key={part.key} part={part} variants={props.variants} />
      ))}
      {optionalParts.length > 0 && (
        <>
          <AdditionalOptionsHeading>{translate(t => t.package.additionalChoices)}</AdditionalOptionsHeading>
          {optionalParts.map(part => (
            <Part key={part.key} part={part} variants={props.variants} />
          ))}
        </>
      )}
    </Parts>
  );
}

const BundleVariantSelector = styled(ProductVariantSelector, {
  margin: { y: t => t.sharedSpacing.xs },
});

const PartContainer = styled.div({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const StockStatusContainer = styled.div({
  margin: { bottom: t => t.sharedSpacing.m },
});

const AdditionalOptionsHeading = styled.h4({
  fontSize: t => t.sharedFontSize.s,
  fontWeight: 500,
  margin: { bottom: 0, top: t => t.sharedSpacing.m },
  textAlign: 'left',
});

const Parts = styled.div({
  marginBottom: t => t.sharedSpacing.xs,
});
